// ─── Kerchief slot geometry ───────────────────────────────────────────────────
//
// Shared by KerchiefView (camper kerchief rendering) and the admin Kerchief
// Layout editor so both place badges in identical positions.
//
// Layout: 12 across top, 8 per side (ends shared), 1 apex = 25 total
//
// Triangle CSS container: width W, height 0.5W (paddingBottom: 50%)
// SVG viewBox: "0 0 100 50" mapped onto this container
// CSS % conversion: left% = svgX, top% = svgY * 2
//
// Slot positions (25 unique slots):
//   Top row  : 12 badges, y = TOP_Y_PCT, x from TOP_X_MIN to TOP_X_MAX
//   Left side: 8 badges — top-left corner (shared with row slot 0) +
//              6 interior + apex (shared with right side)
//   Right side: 8 badges — top-right corner (shared with row slot 11) +
//              6 interior + apex
//   Apex     : 1 badge at the bottom tip (shared end of both sides)

export const TOP_Y_PCT    = 13;   // CSS top % for the top row
export const TOP_X_MIN    = 17;   // CSS left % for leftmost top-row slot (= left side corner)
export const TOP_X_MAX    = 83;   // CSS left % for rightmost top-row slot (= right side corner)
export const TOP_MAX      = 12;   // 12 badges in the top row (includes the two end/corner badges)
export const SIDE_STEPS   = 7;    // segments per side edge; interior slots = SIDE_STEPS - 1 = 6
export const APEX_LEFT    = 50;   // CSS left % for the apex badge
export const APEX_TOP     = 80;   // CSS top % for the apex badge

export interface SlotPos { left: number; top: number; region: "top" | "left" | "right" }

// ─── Fixed clockwise print layout (Kerchief Layout editor) ───────────────────
//
// The physical kerchief has 26 printed icon positions, numbered clockwise:
//   1–11  across the top, left to right
//   12–18 down the right edge
//   19    the bottom tip
//   20–26 back up the left edge (26 ends adjacent to 1)

export const KERCHIEF_SLOT_COUNT = 26;

const CLOCKWISE_TOP_COUNT = 11;
const CLOCKWISE_EDGE_STEPS = 8; // segments per side edge; interior slots = 7

/** Overrides for views with their own margins (e.g. the printable summary). */
export interface SlotGeometry {
  topY?: number;
  xMin?: number;
  xMax?: number;
  apexLeft?: number;
  apexTop?: number;
}

export function computeClockwiseSlots({
  topY = TOP_Y_PCT,
  xMin = TOP_X_MIN,
  xMax = TOP_X_MAX,
  apexLeft = APEX_LEFT,
  apexTop = APEX_TOP,
}: SlotGeometry = {}): SlotPos[] {
  const slots: SlotPos[] = [];

  // 1–11: top row, left → right
  const pitch = (xMax - xMin) / (CLOCKWISE_TOP_COUNT - 1);
  for (let i = 0; i < CLOCKWISE_TOP_COUNT; i++) {
    slots.push({ left: xMin + i * pitch, top: topY, region: "top" });
  }

  // 12–18: right edge, top-right corner → tip
  for (let i = 1; i < CLOCKWISE_EDGE_STEPS; i++) {
    const t = i / CLOCKWISE_EDGE_STEPS;
    slots.push({
      left: xMax + t * (apexLeft - xMax),
      top:  topY + t * (apexTop  - topY),
      region: "right",
    });
  }

  // 19: the tip
  slots.push({ left: apexLeft, top: apexTop, region: "top" });

  // 20–26: left edge, tip → top-left corner
  for (let i = 1; i < CLOCKWISE_EDGE_STEPS; i++) {
    const t = i / CLOCKWISE_EDGE_STEPS;
    slots.push({
      left: apexLeft + t * (xMin - apexLeft),
      top:  apexTop  + t * (topY - apexTop),
      region: "left",
    });
  }

  return slots;
}

// ─── Slot assignment ─────────────────────────────────────────────────────────
//
// A printed activity (onKerchief !== false) stores its 0-based slot directly
// in sortOrder, so slots can be empty. Write-ins sort at KERCHIEF_SLOT_COUNT
// and above. Mirrors assignKerchiefSlots() in backend/src/services/kerchiefSlots.ts.

export interface KerchiefLayoutResult<T> {
  /** Exactly KERCHIEF_SLOT_COUNT entries; null = empty slot. */
  slots: (T | null)[];
  writeIns: T[];
  /** True when stored positions had to be adjusted (collision or overflow). */
  adjusted: boolean;
}

/**
 * Places printed activities at the slot their sortOrder names. Out-of-range
 * or colliding positions (data never saved through the layout editor) fall
 * into the first free slot; anything that still doesn't fit overflows to the
 * write-ins. `items` must be in sortOrder order.
 */
export function layoutKerchief<T extends { sortOrder?: number; onKerchief?: boolean }>(
  items: T[]
): KerchiefLayoutResult<T> {
  const slots: (T | null)[] = new Array(KERCHIEF_SLOT_COUNT).fill(null);
  const pending: T[] = [];
  for (const a of items) {
    if (a.onKerchief === false) continue;
    const s = a.sortOrder;
    if (s != null && Number.isInteger(s) && s >= 0 && s < KERCHIEF_SLOT_COUNT && !slots[s]) {
      slots[s] = a;
    } else {
      pending.push(a);
    }
  }
  const overflow: T[] = [];
  let next = 0;
  for (const a of pending) {
    while (next < KERCHIEF_SLOT_COUNT && slots[next]) next++;
    if (next < KERCHIEF_SLOT_COUNT) slots[next] = a;
    else overflow.push(a);
  }
  return {
    slots,
    writeIns: [...overflow, ...items.filter((a) => a.onKerchief === false)],
    adjusted: pending.length > 0,
  };
}

export function computeSlots(count: number): SlotPos[] {
  const slots: SlotPos[] = [];

  // ── Top row (up to 12) ───────────────────────────────────────────────────
  // Fixed pitch so adjacent badges always have the same spacing (~6.545 %),
  // with the group centered horizontally on the kerchief.
  const topCount = Math.min(count, TOP_MAX);
  const pitch  = (TOP_X_MAX - TOP_X_MIN) / (TOP_MAX - 1);
  const span   = topCount <= 1 ? 0 : (topCount - 1) * pitch;
  const startX = topCount === 1 ? 50 : (TOP_X_MIN + TOP_X_MAX) / 2 - span / 2;
  for (let i = 0; i < topCount; i++) {
    slots.push({ left: startX + i * pitch, top: TOP_Y_PCT, region: "top" });
  }

  let remaining = count - topCount;
  if (remaining === 0) return slots;

  const sideMax = SIDE_STEPS - 1; // 6 interior positions per side

  // ── Left interior (up to 6, t = 1/7 … 6/7 from corner to apex) ──────────
  const leftCount = Math.min(Math.ceil(remaining / 2), sideMax);
  for (let i = 0; i < leftCount; i++) {
    const t = (i + 1) / SIDE_STEPS;
    slots.push({
      left: TOP_X_MIN + t * (APEX_LEFT - TOP_X_MIN),
      top:  TOP_Y_PCT  + t * (APEX_TOP  - TOP_Y_PCT),
      region: "left",
    });
  }

  remaining -= leftCount;
  if (remaining === 0) return slots;

  // ── Right interior (up to 6, t = 1/7 … 6/7 from corner to apex) ─────────
  const rightCount = Math.min(remaining, sideMax);
  for (let i = 0; i < rightCount; i++) {
    const t = (i + 1) / SIDE_STEPS;
    slots.push({
      left: TOP_X_MAX + t * (APEX_LEFT - TOP_X_MAX),
      top:  TOP_Y_PCT  + t * (APEX_TOP  - TOP_Y_PCT),
      region: "right",
    });
  }

  remaining -= rightCount;

  // ── Apex (1) — shared bottom tip of both sides ───────────────────────────
  if (remaining > 0) {
    slots.push({ left: APEX_LEFT, top: APEX_TOP, region: "top" });
  }

  return slots;
}
